import {
	InfoCircleOutlined,
	MinusOutlined,
	PlusOutlined,
	StopOutlined,
} from '@ant-design/icons';
import {
	Button,
	Checkbox,
	Input,
	InputNumber,
	message,
	Modal,
	Popover,
	Spin,
	Alert,
	Tooltip,
	Typography,
} from 'antd';
import { Component, useState, useEffect, useCallback, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import dayjs from 'dayjs';
import { USER_ROLES } from 'src/_shared/constants/userRolesEnum.js';
import { QueryPointsBalancesByUserId } from 'src/_shared/api/graphql/custom/dashboard/queryPointsBalancesByUserIdGraphql.js';
import {
	CreatePointsRevokeEvent,
	CreatePointsTriggerEvent,
	GetPointsSettings,
	QueryPointsEventsByUserId,
	formatDate,
	formatDateUTC,
	formatUserName,
	getEventAttribution,
	isRevocableEvent,
} from 'src/new-points/pointsSettingsHelpers.js';

const { Text } = Typography;

const formatActionType = (actionType, event) => {
	const isRevokedDebit =
		event.direction === 'DEBIT' &&
		event.sourceType === 'Revoke' &&
		actionType !== 'POINTS_SPENT';
	const revokerName = formatUserName(event.actorUser);
	const revokerEmail = event.actorUser?.emailAddress;
	const revokedSuffix = isRevokedDebit
		? revokerName
			? ` that was revoked by ${revokerName}${
					revokerEmail ? ` (${revokerEmail})` : ''
				}`
			: ' that was revoked'
		: '';

	let reason;
	switch (actionType) {
		case 'REFERRAL_SENT':
			reason = 'sending a referral';
			break;
		case 'REFERRAL_GENERAL_SENT':
			reason = 'a referral that was accepted';
			break;
		case 'REFERRAL_TO_ACCEPTED':
			reason = 'a referral being accepted';
			break;
		case 'REFERRAL_TO_INTERVIEWING':
			reason = 'a referral moving to interviewing';
			break;
		case 'REFERRAL_TO_HIRED':
			reason = 'a referral being hired';
			break;
		case 'REFERRAL_LINK_SHARED':
			reason = 'sharing a referral link';
			break;
		case 'REFERRAL_FROM_SHARE':
			reason = 'a referral from a social media share';
			break;
		case 'FIRST_MOBILE_LOGIN':
			reason = 'a first mobile login';
			break;
		case 'WEEKLY_LOGIN_STREAK':
			reason = 'a weekly login streak';
			break;
		case 'RECOGNITION_SENT':
			reason = 'sending a recognition';
			break;
		case 'RECOGNITION_RECEIVED':
			reason = 'receiving a recognition';
			break;
		case 'POINTS_FROM_BADGE':
			reason = 'the points value set on a received recognition';
			break;
		case 'POINTS_SPENT':
			reason =
				event.direction === 'CREDIT' ? 'a points refund' : 'spending points';
			break;
		default:
			reason = actionType.toLowerCase().replace(/_/g, ' ');
			break;
	}

	return `${reason}${revokedSuffix}`;
};

// A small info icon that reveals event details (revoke reason, who revoked)
// in a hover popover.
const DetailsPopover = ({ content }) => (
	<Popover content={content} trigger="hover" placement="top">
		<InfoCircleOutlined
			style={{ marginLeft: 4, color: '#8c8c8c', cursor: 'pointer' }}
		/>
	</Popover>
);

// Renders the trailing "(Revoked)" label / details icon / "Added by …"
// enrichment for a compact log row. For reversal DEBIT entries the revoker's
// name & email are rendered inline in the sentence (see formatActionType); the
// icon's popover carries the reason.
const renderEnrichment = (event) => {
	const attribution = getEventAttribution(event);
	if (!attribution) return null;
	const name = formatUserName(attribution.user);
	const email = attribution.user?.emailAddress;
	const reason = attribution.reason;

	if (attribution.type === 'REVOKE_DEBIT') {
		if (!reason) return null;
		return (
			<DetailsPopover
				content={
					<div style={{ maxWidth: 280 }}>
						<strong>Reason:</strong> {reason}
					</div>
				}
			/>
		);
	}

	if (attribution.type === 'REVOKED_ORIGINAL') {
		return (
			<>
				<span style={{ color: '#e74c3c', fontWeight: 600 }}> (Revoked)</span>
				{(name || reason) && (
					<DetailsPopover
						content={
							<div style={{ maxWidth: 280 }}>
								{name && (
									<div>
										<strong>Revoked by:</strong> {name}
										{email ? ` (${email})` : ''}
									</div>
								)}
								{reason && (
									<div style={{ marginTop: name ? 4 : 0 }}>
										<strong>Reason:</strong> {reason}
									</div>
								)}
							</div>
						}
					/>
				)}
			</>
		);
	}

	// TRIGGERED (custom points added via createPointsTriggerEvent)
	if (!name) return null;
	const addedByLabel = (
		<span
			style={{
				color: '#595959',
				...(reason ? { cursor: 'help', borderBottom: '1px dotted #999' } : {}),
			}}
		>
			{` Added by ${name}.`}
		</span>
	);
	if (!reason) return addedByLabel;
	return (
		<Popover
			content={
				<div style={{ maxWidth: 280 }}>
					<strong>Reason:</strong> {reason}
				</div>
			}
			trigger="hover"
			placement="top"
		>
			{addedByLabel}
		</Popover>
	);
};

const getCurrentPeriodKey = (primaryPeriod) => {
	const now = dayjs();
	switch (primaryPeriod) {
		case 'YEARLY':
			return now.format('YYYY');
		case 'QUARTERLY': {
			const quarter = Math.ceil((now.month() + 1) / 3);
			return `${now.format('YYYY')}-Q${quarter}`;
		}
		case 'MONTHLY':
			return now.format('YYYY-MM');
		case 'ALLTIME':
		default:
			return null;
	}
};

const getPeriodStartDate = (primaryPeriod) => {
	const now = dayjs();
	switch (primaryPeriod) {
		case 'YEARLY':
			return now.startOf('year');
		case 'QUARTERLY': {
			const quarterStartMonth = Math.floor(now.month() / 3) * 3;
			return now.month(quarterStartMonth).startOf('month');
		}
		case 'MONTHLY':
			return now.startOf('month');
		case 'ALLTIME':
		default:
			return null;
	}
};

const EmployeePointsLog = ({ selectedUserId, currentUser }) => {
	const apolloClient = useApolloClient();
	const [pointsEvents, setPointsEvents] = useState([]);
	const [nextToken, setNextToken] = useState(null);
	const [loadingEvents, setLoadingEvents] = useState(false);
	const [balance, setBalance] = useState(null);
	const [loadingBalance, setLoadingBalance] = useState(false);
	const [periodStartDate, setPeriodStartDate] = useState(null);
	const [primaryPeriod, setPrimaryPeriod] = useState('ALLTIME');
	const [excludeSpendablePointsFromReset, setExcludeSpendablePointsFromReset] =
		useState(false);
	const [revokeTarget, setRevokeTarget] = useState(null);
	const [revokeReason, setRevokeReason] = useState('');
	const [revoking, setRevoking] = useState(false);
	const [addOpen, setAddOpen] = useState(false);
	const [addAmount, setAddAmount] = useState(null);
	const [addReason, setAddReason] = useState('');
	const [addSpendable, setAddSpendable] = useState(false);
	const [addingPoints, setAddingPoints] = useState(false);
	const [hoveredIndex, setHoveredIndex] = useState(null);
	const logContainerRef = useRef(null);
	const loadingEventsRef = useRef(false);
	const nextTokenRef = useRef(null);

	const companyId = currentUser?.companyId;

	const fetchPointsEvents = useCallback(
		async (reset = false) => {
			if (!selectedUserId || loadingEventsRef.current) return;

			try {
				loadingEventsRef.current = true;
				setLoadingEvents(true);
				const { data } = await apolloClient.query({
					query: QueryPointsEventsByUserId,
					variables: {
						userId: selectedUserId,
						first: 50,
						after: reset ? null : nextTokenRef.current,
					},
					fetchPolicy: 'network-only',
				});

				const result = data?.queryPointsEventsByUserId;
				if (result) {
					const filtered = (result.items || []).filter(
						(item) => item.companyId === companyId
					);
					setPointsEvents((prev) =>
						reset ? filtered : [...prev, ...filtered]
					);
					nextTokenRef.current = result.nextToken;
					setNextToken(result.nextToken);
				}
			} catch (error) {
				console.error('Error fetching points events:', error);
			} finally {
				loadingEventsRef.current = false;
				setLoadingEvents(false);
			}
		},
		[apolloClient, selectedUserId, companyId]
	);

	const fetchBalance = useCallback(async () => {
		if (!selectedUserId || !companyId) return;

		try {
			setLoadingBalance(true);
			const [balancesResponse, settingsResponse] = await Promise.all([
				apolloClient.query({
					query: QueryPointsBalancesByUserId,
					variables: { userId: selectedUserId, first: 100 },
					fetchPolicy: 'network-only',
				}),
				apolloClient.query({
					query: GetPointsSettings,
					variables: { companyId },
					fetchPolicy: 'network-only',
				}),
			]);

			const balances = (
				balancesResponse?.data?.queryPointsBalancesByUserId?.items ?? []
			).filter((b) => b.companyId === companyId);
			const settings = settingsResponse?.data?.getPointsSettings;
			const primaryPeriod = settings?.primaryPeriod ?? 'ALLTIME';
			const excludeSpendableFromReset =
				settings?.excludeSpendablePointsFromReset ?? false;

			setPrimaryPeriod(primaryPeriod);
			setPeriodStartDate(getPeriodStartDate(primaryPeriod));
			setExcludeSpendablePointsFromReset(excludeSpendableFromReset);

			let matchedBalance;
			if (primaryPeriod === 'ALLTIME') {
				matchedBalance = balances.find((b) => b.isAllTime);
			} else if (excludeSpendableFromReset) {
				// Spendable points ignore the reset period, so Spent and Current
				// Spendable come from the all-time balance while Non-Spendable still
				// resets per period. Earned is recomputed as the all-time earned that
				// remains spendable plus the period's non-spendable earned.
				const periodKey = getCurrentPeriodKey(primaryPeriod);
				const aT = balances.find((b) => b.isAllTime);
				const pK = balances.find((b) => b.periodKey === periodKey);
				matchedBalance = {
					spent: aT?.spent ?? 0,
					currentSpendable: aT?.currentSpendable ?? 0,
					nonSpendable: pK?.nonSpendable ?? 0,
					// Clamp to >= 0: refund credits or missing period/all-time data
					// can push the computed earned negative, which would be nonsense
					// to display. This value is presentational only.
					earned: Math.max(
						0,
						(aT?.earned ?? 0) -
							(aT?.nonSpendable ?? 0) +
							((pK?.earned ?? 0) -
								((pK?.currentSpendable ?? 0) + (pK?.spent ?? 0)))
					),
				};
			} else {
				const periodKey = getCurrentPeriodKey(primaryPeriod);
				matchedBalance = balances.find((b) => b.periodKey === periodKey);
			}

			setBalance(matchedBalance || null);
		} catch (error) {
			console.error('Error fetching points balance:', error);
		} finally {
			setLoadingBalance(false);
		}
	}, [apolloClient, selectedUserId, companyId]);

	useEffect(() => {
		fetchPointsEvents(true);
		fetchBalance();
	}, [fetchPointsEvents, fetchBalance]);

	const handleScroll = useCallback(
		(e) => {
			const { scrollTop, scrollHeight, clientHeight } = e.target;
			if (
				scrollHeight - scrollTop <= clientHeight * 1.5 &&
				nextTokenRef.current &&
				!loadingEventsRef.current
			) {
				fetchPointsEvents(false);
			}
		},
		[fetchPointsEvents]
	);

	const closeRevokeModal = () => {
		if (revoking) return;
		setRevokeTarget(null);
		setRevokeReason('');
	};

	const handleRevoke = async () => {
		if (!revokeTarget?.id) {
			message.error('Cannot revoke: this event is missing an id.');
			return;
		}

		const reason = revokeReason.trim();
		if (!reason) return;

		try {
			setRevoking(true);
			await apolloClient.mutate({
				mutation: CreatePointsRevokeEvent,
				variables: {
					input: {
						companyId,
						originalPointsEventId: revokeTarget.id,
						revokedByUserId: currentUser?.id,
						reason,
					},
				},
			});
			message.success('Points event revoked.');
			setRevokeTarget(null);
			setRevokeReason('');
			// The reversal entry and balance updates are produced asynchronously by
			// the points engine, so give it a moment before refreshing.
			setTimeout(() => {
				fetchPointsEvents(true);
				fetchBalance();
			}, 1500);
		} catch (error) {
			console.error('Error revoking points event:', error);
			message.error('Failed to revoke points event.');
		} finally {
			setRevoking(false);
		}
	};

	const closeAddModal = () => {
		if (addingPoints) return;
		setAddOpen(false);
		setAddAmount(null);
		setAddReason('');
		setAddSpendable(false);
	};

	const handleAddPoints = async () => {
		const amount = Number(addAmount);
		const reason = addReason.trim();
		if (!Number.isInteger(amount) || amount <= 0) {
			message.error('Enter an amount greater than 0.');
			return;
		}

		if (!reason) {
			message.error('A reason is required.');
			return;
		}

		try {
			setAddingPoints(true);
			await apolloClient.mutate({
				mutation: CreatePointsTriggerEvent,
				variables: {
					input: {
						companyId,
						userId: selectedUserId,
						amount,
						isSpendable: addSpendable,
						message: reason,
						triggeredByUserId: currentUser?.id,
					},
				},
			});
			message.success('Custom points added.');
			setAddOpen(false);
			setAddAmount(null);
			setAddReason('');
			setAddSpendable(false);
			// The points event and balances are produced asynchronously by the
			// points engine, so give it a moment before refreshing.
			setTimeout(() => {
				fetchPointsEvents(true);
				fetchBalance();
			}, 1500);
		} catch (error) {
			console.error('Error adding custom points:', error);
			message.error('Failed to add custom points.');
		} finally {
			setAddingPoints(false);
		}
	};

	return (
		<>
			<div className="ed-card-header bg-14D6D3">
				<span className="card-header-text">Points Balance</span>
				{loadingBalance ? (
					<Spin size="small" style={{ marginTop: 4 }} />
				) : (
					<h3>{balance?.earned ?? 0}</h3>
				)}
			</div>
			<div className="ed-card-body">
				{!loadingBalance && (
					<div
						style={{
							display: 'grid',
							gridTemplateColumns: '1fr 1fr',
							gap: '8px',
							marginBottom: '12px',
						}}
					>
						<div>
							<Text type="secondary" style={{ fontSize: 12 }}>
								Earned
							</Text>
							<div style={{ fontWeight: 600 }}>{balance?.earned ?? 0}</div>
						</div>
						<div>
							<Text type="secondary" style={{ fontSize: 12 }}>
								Spent
							</Text>
							<div style={{ fontWeight: 600 }}>{balance?.spent ?? 0}</div>
						</div>
						<div>
							<Text type="secondary" style={{ fontSize: 12 }}>
								Current Spendable
							</Text>
							<div style={{ fontWeight: 600 }}>
								{balance?.currentSpendable ?? 0}
							</div>
						</div>
						<div>
							<Text type="secondary" style={{ fontSize: 12 }}>
								Non-Spendable
							</Text>
							<div style={{ fontWeight: 600 }}>
								{balance?.nonSpendable ?? 0}
							</div>
						</div>
					</div>
				)}
				<div
					style={{
						borderTop: '1px solid #f0f0f0',
						paddingTop: '8px',
						marginBottom: '4px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
					}}
				>
					<Text strong style={{ fontSize: 13 }}>
						Points Log
					</Text>
					<Button
						type="link"
						size="small"
						icon={<PlusOutlined />}
						onClick={() => setAddOpen(true)}
						style={{ padding: 0, fontSize: 12, height: 'auto' }}
					>
						Add Custom Points
					</Button>
				</div>
				<div
					ref={logContainerRef}
					onScroll={handleScroll}
					style={{
						maxHeight: '175px',
						overflowY: 'auto',
					}}
				>
					{(() => {
				const filteredEvents = periodStartDate
					? pointsEvents.filter(
							(event) =>
								!dayjs(event.createdAt).isBefore(periodStartDate) ||
								(excludeSpendablePointsFromReset && event.isSpendable)
						)
					: pointsEvents;
				const periodSuffix =
					primaryPeriod === 'QUARTERLY'
						? ' for the current quarter'
						: primaryPeriod === 'MONTHLY'
							? ' for the current month'
							: primaryPeriod === 'YEARLY'
								? ' for the current year'
								: '';
				return loadingEvents && filteredEvents.length === 0 ? (
						<div style={{ textAlign: 'center', padding: '20px' }}>
							<Spin size="small" />
						</div>
					) : filteredEvents.length === 0 ? (
						<Alert
							message={`No points events yet${periodSuffix}`}
							type="info"
							showIcon
							style={{ fontSize: 12 }}
						/>
					) : (
						<>
							{filteredEvents.map((event, index) => {
								const isDebit = event.direction === 'DEBIT';
								const isRevoked = event.revoked === true;
								const verb = isDebit ? 'Deducted' : 'Earned';
								const pointsColor = isDebit ? '#e74c3c' : '#27ae60';

								return (
									<div
										key={`${event.createdAt}-${index}`}
										onMouseEnter={() => setHoveredIndex(index)}
										onMouseLeave={() =>
											setHoveredIndex((cur) => (cur === index ? null : cur))
										}
										style={{
											display: 'flex',
											alignItems: 'flex-start',
											gap: 4,
											padding: '8px 0',
											borderBottom:
												index < filteredEvents.length - 1
													? '1px solid #f0f0f0'
													: 'none',
											lineHeight: '1.5',
											fontSize: '12px',
											opacity: isRevoked ? 0.45 : 1,
										}}
									>
										<span style={{ flex: 1 }}>
											<Tooltip
												title={formatDateUTC(event.createdAt)}
												placement="top"
											>
												<span
													style={{
														cursor: 'help',
														borderBottom: '1px dotted #999',
													}}
												>
													{formatDate(event.createdAt)}
												</span>
											</Tooltip>
											{' — '}
											{verb}{' '}
											<strong style={{ color: pointsColor }}>
												{event.amount} pts
											</strong>{' '}
											for {formatActionType(event.actionType, event)}.
											{renderEnrichment(event)}
										</span>
										{isRevocableEvent(event) && (
											<span
												style={{
													opacity: hoveredIndex === index ? 1 : 0,
													transform:
														hoveredIndex === index ? 'scale(1)' : 'scale(0.8)',
													transition:
														'opacity 0.2s ease, transform 0.2s ease',
													pointerEvents:
														hoveredIndex === index ? 'auto' : 'none',
												}}
											>
												<Tooltip title="Revoke this event">
													<Button
														type="text"
														size="small"
														danger
														icon={<StopOutlined />}
														style={{ minWidth: 'auto', padding: '0 4px' }}
														onClick={() => {
															setRevokeTarget(event);
															setRevokeReason('');
														}}
													/>
												</Tooltip>
											</span>
										)}
									</div>
								);
							})}
							{loadingEvents && (
								<div style={{ textAlign: 'center', padding: '12px' }}>
									<Spin size="small" />
								</div>
							)}
							{!loadingEvents && !nextToken && filteredEvents.length > 0 && (
								<div
									style={{
										textAlign: 'center',
										padding: '12px',
										color: '#595959',
										fontSize: 12,
									}}
								>
									<Text type="secondary">{`All entries loaded${periodSuffix}`}</Text>
								</div>
							)}
						</>
					);
				})()}
				</div>
			</div>
			<Modal
				title="Revoke points event"
				open={!!revokeTarget}
				onCancel={closeRevokeModal}
				onOk={handleRevoke}
				okText="Revoke"
				okButtonProps={{
					danger: true,
					loading: revoking,
					disabled: !revokeReason.trim(),
				}}
				cancelButtonProps={{ disabled: revoking }}
				maskClosable={!revoking}
			>
				<p>
					This will revoke the selected points event and reverse the points it
					granted. A reason is required.
				</p>
				<Input.TextArea
					rows={3}
					value={revokeReason}
					onChange={(e) => setRevokeReason(e.target.value)}
					placeholder="Reason for revoking this event"
					maxLength={500}
					disabled={revoking}
				/>
			</Modal>
			<Modal
				title="Add Custom Points"
				open={addOpen}
				onCancel={closeAddModal}
				onOk={handleAddPoints}
				okText="Add Points"
				okButtonProps={{
					loading: addingPoints,
					disabled: !(Number(addAmount) > 0) || !addReason.trim(),
				}}
				cancelButtonProps={{ disabled: addingPoints }}
				maskClosable={!addingPoints}
			>
				<div style={{ marginBottom: 12 }}>
					<div style={{ marginBottom: 4 }}>
						<Text strong>Amount</Text>
					</div>
					<InputNumber
						min={1}
						precision={0}
						value={addAmount}
						onChange={setAddAmount}
						placeholder="Number of points"
						style={{ width: '100%' }}
						disabled={addingPoints}
					/>
				</div>
				<div style={{ marginBottom: 12 }}>
					<div style={{ marginBottom: 4 }}>
						<Text strong>Reason</Text>
					</div>
					<Input.TextArea
						rows={3}
						value={addReason}
						onChange={(e) => setAddReason(e.target.value)}
						placeholder="Reason for adding these points"
						maxLength={500}
						disabled={addingPoints}
					/>
				</div>
				<Checkbox
					checked={addSpendable}
					onChange={(e) => setAddSpendable(e.target.checked)}
					disabled={addingPoints}
				>
					Spendable
				</Checkbox>
			</Modal>
		</>
	);
};

class EmployeePointsInfo extends Component {
	constructor(props) {
		super(props);
		this.state = {
			point: '',
			reason: '',
		};
	}

	onChangePoints = (e) => {
		const { value } = e.target;
		this.setState({
			point: value,
		});
	};

	onChangeReason = (e) => {
		const { value } = e.target;
		this.setState({
			reason: value,
		});
	};

	validatePoints = (inputValue) => {
		// RegExp method to test if string is number
		const isNumber = /^\d+$/.test(inputValue);
		const isGreaterThanZero = Number.parseInt(inputValue, 10) > 0;

		return isNumber && isGreaterThanZero;
	};

	Add = () => {
		const {
			handlePoints,
			companyId,
			userId,
			currentUserEmail,
			selectedUserId,
		} = this.props;
		const { point, reason } = this.state;
		if (this.validatePoints(point)) {
			if (reason === '') {
				message.error('Adjustment reasons must not be blank.');
			} else {
				const input = {
					companyId,
					userId: selectedUserId,
					event: 'byAdmin',
					operation: 'add',
					points: point,
					note: reason,
					adminId: userId,
					adminEmail: currentUserEmail,
				};

				handlePoints(input);
				this.setState({
					point: '',
					reason: '',
				});
				message.success(`Added ${point} point(s).`);
			}
		} else {
			message.error('Please enter a valid number of points.');
		}
	};

	Subtract = () => {
		const {
			handlePoints,
			companyId,
			userId,
			selectedUserId,
			currentUserEmail,
		} = this.props;
		const { point, reason } = this.state;
		if (this.validatePoints(point)) {
			if (reason === '') {
				message.error('Adjustment reasons must not be blank.');
			} else {
				const input = {
					companyId,
					userId: selectedUserId,
					event: 'byAdmin',
					operation: 'subtract',
					points: point,
					note: reason,
					adminId: userId,
					adminEmail: currentUserEmail,
				};

				handlePoints(input);
				this.setState({
					point: '',
					reason: '',
				});
				message.success(`Subtracted ${point} point(s).`);
			}
		} else {
			message.error('Please enter a valid number of points.');
		}
	};

	render() {
		const { currentUser, points, selectedUserId } = this.props;
		const managerEmployees = currentUser.company
			?.disableManagerPermissionsByType
			? JSON.parse(currentUser.company.disableManagerPermissionsByType)
					.managerEmployees
			: 'hidden';
		const recognitionCurrency =
			currentUser.company?.recognitionSettings?.rewardsEarnedMechanism ===
			'recognition';
		const defaultBudget =
			currentUser.company?.recognitionSettings?.baseSnappyBudget;
		const newPointsEarlyPreview = currentUser?.featureFlags?.includes(
			'newPointsEarlyPreview'
		);
		const isManagerPermissionDisabled = Boolean(
			managerEmployees !== 'edit' && currentUser?.role === USER_ROLES.MANAGER
		);

		// New Points Preview replaces both the budget adjust or old points add/subtract card
		if (newPointsEarlyPreview) {
			return (
				<EmployeePointsLog
					selectedUserId={selectedUserId}
					currentUser={currentUser}
				/>
			);
		}

		return (
			<>
				<div className="ed-card-header bg-14D6D3">
					<span className="card-header-text">
						{recognitionCurrency ? 'Total Budget:' : 'Total Points:'}
					</span>
					<h3>{recognitionCurrency ? `$${defaultBudget}` : points}</h3>
				</div>
				<div className="ed-card-body">
					<div className="card-body-detail">
						<label />
						<div className="custom-form-group">
							<label>
								{recognitionCurrency ? 'Adjust Budget:' : 'Adjust Points:'}
							</label>
						</div>
					</div>
					{isManagerPermissionDisabled ? (
						<span>Manager Permissions have been set to Read-Only.</span>
					) : (
						<>
							<div className="card-body-detail">
								<label>{recognitionCurrency ? 'Budget:' : 'Points:'}</label>
								<div className="custom-form-group">
									<Input
										className="custom-input"
										placeholder={recognitionCurrency ? 'Budget' : '# of Points'}
										value={this.state.point}
										onChange={this.onChangePoints.bind(this)}
									/>
								</div>
							</div>
							<div className="card-body-detail">
								<label>Note:</label>
								<div className="custom-form-group">
									<Input
										className="custom-input"
										placeholder="Adjustment Reasons"
										value={this.state.reason}
										onChange={this.onChangeReason.bind(this)}
									/>
								</div>
							</div>
							<div className="card-body-action">
								<Button
									type="link"
									className="add-btn"
									size="small"
									onClick={this.Subtract.bind(this)}
								>
									<span className="icon-circle">
										<MinusOutlined />
									</span>
									<span>Subtract</span>
								</Button>
								<Button
									type="link"
									className="add-btn"
									size="small"
									onClick={this.Add.bind(this)}
								>
									<span className="icon-circle">
										<i className="icon-plus" />
									</span>
									<span>Add</span>
								</Button>
							</div>
						</>
					)}
				</div>
			</>
		);
	}
}

export default EmployeePointsInfo;
